import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Edit, Building } from 'lucide-react';
import useAuth from 'auth/auth';
import { Manufacturer } from 'services/api/domains/manufacturers/types';
import { Facility } from 'services/api/domains/facilities/types';
import { useManufacturersPage } from '../services/api/domains/manufacturers/hooks';
import {
  useMergeManufacturersSuggestions,
  useMergeManufacturersPreview,
  useMergeManufacturers,
} from '../services/api/domains/manufacturers/hooks';
import GenericMergeModal from '../components/modals/GenericMergeModal';
import {
  useFacilitiesPage,
  useUpdateFacility,
} from '../services/api/domains/facilities/hooks';
import ManufacturerModal, {
  ModalMode,
} from '../components/modals/ManufacturerModal';
import FacilityModal from '../components/modals/FacilityModal';
import {
  UnifiedSearchBar,
  UnifiedSearchState,
} from 'components/UnifiedSearchBar';
import { createEmptyAdvancedQuery } from 'components/TableSearch/types';
import { manufacturersFieldConfig } from '../config/searchConfigs';
import { MANUFACTURER_PRESETS } from '../domains/manufacturers/filterPresets';
import LgmDataGrid from 'components/LgmDataGrid';
import { DensityToggle } from 'components/DensityToggle';
import { useTableDensity } from 'shared/hooks/useTableDensity';
import {
  toDataGridColumns,
  getDefaultColumnVisibility,
} from '../domains/manufacturers/adapters';
import {
  GridColDef,
  GridFilterModel,
  GridSortModel,
  GridPaginationModel,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid-premium';
import {
  gridFilterToAdvancedQuery,
  mergeAdvancedQueries,
} from 'utils/dataGridHelpers';

const STORAGE_KEY_MFR_FILTER_PRESETS = 'lgm_mfr_filter_presets';
const STORAGE_KEY_MFR_FILTER_ACTIVE = 'lgm_mfr_filter_active';

const PAGE_SIZE = 50;

const ManufacturerReview: React.FC = () => {
  const { isViewer, isAdmin } = useAuth();
  const { density } = useTableDensity();
  const rowHeight = density === 'compact' ? 36 : 52;

  // Search state — managed by UnifiedSearchBar, reflected here for API params
  const [searchState, setSearchState] = useState<UnifiedSearchState>({
    mode: 'basic',
    basicTerm: '',
    advancedQuery: createEmptyAdvancedQuery(),
  });

  // DataGrid state
  const [gridFilterModel, setGridFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [gridSortModel, setGridSortModel] = useState<GridSortModel>([]);
  const [gridPaginationModel, setGridPaginationModel] =
    useState<GridPaginationModel>({
      page: 0,
      pageSize: PAGE_SIZE,
    });
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(getDefaultColumnVisibility());

  const manufacturersParams = useMemo(() => {
    let finalAdvancedQuery = createEmptyAdvancedQuery();

    // Start with UnifiedSearchBar query (basic or advanced)
    if (searchState.mode === 'advanced') {
      finalAdvancedQuery = searchState.advancedQuery;
    }

    if (gridFilterModel.items.length > 0) {
      const gridQuery = gridFilterToAdvancedQuery(
        gridFilterModel,
        manufacturersFieldConfig
      );

      if (gridQuery.filterGroups.length > 0) {
        finalAdvancedQuery = mergeAdvancedQueries(
          finalAdvancedQuery,
          gridQuery
        );
      }
    }

    // Check if there are actual filter rules, not just empty groups
    const hasActualFilters = finalAdvancedQuery.filterGroups.some(
      group => group.rules && group.rules.length > 0
    );

    const params = {
      search:
        searchState.mode === 'basic'
          ? searchState.basicTerm.trim() || undefined
          : undefined,
      advancedSearch: hasActualFilters ? finalAdvancedQuery : undefined,
      sortBy:
        gridSortModel.length > 0
          ? (gridSortModel[0].field as keyof Manufacturer)
          : undefined,
      sortDirection:
        gridSortModel.length > 0
          ? (gridSortModel[0].sort?.toUpperCase() as 'ASC' | 'DESC')
          : undefined,
      limit: gridPaginationModel.pageSize,
      offset: gridPaginationModel.page * gridPaginationModel.pageSize,
    };

    return params;
  }, [searchState, gridFilterModel, gridSortModel, gridPaginationModel]);

  const {
    update: updateManufacturer,
    delete: deleteManufacturer,
    data: manufacturersData,
    total,
    refetch: refetchManufacturers,
  } = useManufacturersPage(manufacturersParams);
  const { data: facilities, refetch: refetchFacilities } = useFacilitiesPage();

  // Preserve total during loading to prevent DataGrid from resetting pagination
  const [stableTotal, setStableTotal] = useState(total || 0);
  useEffect(() => {
    if (total && total > 0) {
      setStableTotal(total);
    }
  }, [total, stableTotal]);

  // Modal state for ManufacturerModal
  const [manufacturerModalMode, setManufacturerModalMode] =
    useState<ModalMode | null>(null);
  const [selectedManufacturer, setSelectedManufacturer] =
    useState<Manufacturer | null>(null);

  // Facilities modal
  const [openFacilities, setOpenFacilities] = useState<Manufacturer | null>(
    null
  );

  // FacilityModal state
  const [facilityModalMode, setFacilityModalMode] = useState<ModalMode | null>(
    null
  );
  const [selectedFacility, setSelectedFacility] = useState<Facility | null>(
    null
  );

  // Merge modal state
  const [openMergeModal, setOpenMergeModal] = useState(false);
  const [mergeSourceManufacturer, setMergeSourceManufacturer] =
    useState<Manufacturer | null>(null);

  const manufacturerFacilities = useMemo(() => {
    if (!openFacilities || !Array.isArray(openFacilities.facilities)) return [];
    // openFacilities.facilities is a list of FacilitySummary (facility_id, facility_name, ...)
    // facilities is the full list of Facility objects from useFacilitiesPage
    return openFacilities.facilities
      .map(summary =>
        facilities?.find(f => f.facility_id === summary.facility_id)
      )
      .filter(Boolean) as Facility[];
  }, [openFacilities, facilities]);

  const updateFacilityMutation = useUpdateFacility();

  // DataGrid columns from adapter
  const manufacturerColumns = useMemo<GridColDef<Manufacturer>[]>(
    () => toDataGridColumns(),
    []
  );

  // Reset DataGrid page when search changes
  useEffect(() => {
    setGridPaginationModel(prev => ({ ...prev, page: 0 }));
  }, [searchState]);

  const handleSearchChange = useCallback((state: UnifiedSearchState) => {
    setSearchState(state);
  }, []);

  const handleDelete = async (id: number) => {
    if (
      !window.confirm(
        'Delete manufacturer and all related facilities/products?'
      )
    )
      return;
    try {
      await deleteManufacturer.mutate(id);
      refetchManufacturers();
    } catch (err) {
      console.error('❌ Delete failed:', err);
    }
  };

  const loadFacilities = (m: Manufacturer) => {
    setOpenFacilities(m);
    // The hook will automatically fetch data when openFacilities changes
  };

  // New unified modal handlers
  const handleSaveManufacturer = async (manufacturerData: Manufacturer) => {
    try {
      await updateManufacturer.mutate({
        ...manufacturerData,
        facility_ids: Array.isArray(manufacturerData.facilities)
          ? manufacturerData.facilities.map(f => f.facility_id)
          : [],
      });
      setManufacturerModalMode(null);
      setSelectedManufacturer(null);
      refetchManufacturers();
    } catch (err) {
      console.error('❌ Save manufacturer failed:', err);
    }
  };

  const handleCloseManufacturerModal = () => {
    setManufacturerModalMode(null);
    setSelectedManufacturer(null);
  };

  // New unified facility modal handlers
  const handleSaveFacility = async (facilityData: Facility) => {
    try {
      await updateFacilityMutation.mutate(facilityData);
      setFacilityModalMode(null);
      setSelectedFacility(null);
      refetchManufacturers();
      refetchFacilities();
    } catch (err) {
      console.error('❌ Save facility failed:', err);
    }
  };

  return (
    <div style={styles.pageContainerStyle}>
      <div
        style={styles.searchBarContainerStyle}
        data-layout-role="search-panel">
        <UnifiedSearchBar
          fieldConfigs={manufacturersFieldConfig}
          placeholder="Search manufacturers..."
          builtInPresets={MANUFACTURER_PRESETS}
          storageKeyUserPresets={STORAGE_KEY_MFR_FILTER_PRESETS}
          storageKeyActivePreset={STORAGE_KEY_MFR_FILTER_ACTIVE}
          onSearchChange={handleSearchChange}
          actions={<DensityToggle />}
        />
      </div>

      <div style={styles.tableContainerStyle} data-layout-role="table-body">
        <LgmDataGrid<Manufacturer>
          data={manufacturersData || []}
          columns={manufacturerColumns}
          rowCount={stableTotal}
          rowHeight={rowHeight}
          paginationModel={gridPaginationModel}
          onPaginationModelChange={setGridPaginationModel}
          sortModel={gridSortModel}
          onSortModelChange={setGridSortModel}
          filterModel={gridFilterModel}
          onFilterModelChange={setGridFilterModel}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          getRowId={row => row.manufacturer_id}
          onInfo={m => {
            setSelectedManufacturer(m);
            setManufacturerModalMode('VIEW');
          }}
          onEdit={
            !isViewer
              ? m => {
                  setSelectedManufacturer(m);
                  setManufacturerModalMode('EDIT');
                }
              : undefined
          }
          onDelete={
            !isViewer ? m => handleDelete(m.manufacturer_id) : undefined
          }
          onMerge={
            isAdmin
              ? m => {
                  setMergeSourceManufacturer(m);
                  setOpenMergeModal(true);
                }
              : undefined
          }
          actions={(_m: Manufacturer) => [
            {
              icon: <Building size={16} />,
              onAction: (m: Manufacturer) => loadFacilities(m),
              title: 'View Facilities',
              style: styles.facilitiesButtonStyle,
              hoverStyle: {
                color: '#16a34a',
              },
            },
          ]}
        />
      </div>

      {/* ManufacturerModal */}
      {manufacturerModalMode && selectedManufacturer && (
        <ManufacturerModal
          manufacturer={selectedManufacturer}
          initialMode={manufacturerModalMode}
          save={handleSaveManufacturer}
          close={handleCloseManufacturerModal}
        />
      )}

      {/* GenericMergeModal for Manufacturers */}
      {openMergeModal && mergeSourceManufacturer && (
        <GenericMergeModal<Manufacturer>
          source={mergeSourceManufacturer}
          allEntities={manufacturersData}
          config={{
            domainDisplayName: 'Manufacturer',
            domainDisplayNamePlural: 'Manufacturers',
            entityIdField: 'manufacturer_id',
            entityNameField: 'manufacturer_name',
            entityCreatedDateField: 'created_date',
            useMergeSuggestions: useMergeManufacturersSuggestions,
            useMergePreview: useMergeManufacturersPreview,
            useMerge: useMergeManufacturers,
          }}
          onClose={() => {
            setOpenMergeModal(false);
            setMergeSourceManufacturer(null);
          }}
          onSuccess={() => {
            setOpenMergeModal(false);
            setMergeSourceManufacturer(null);
            refetchManufacturers();
          }}
        />
      )}

      {/* Facilities Modal */}
      {openFacilities && (
        <div style={styles.modalOverlayStyle}>
          <div style={styles.facilitiesModalContentStyle}>
            <h2 style={styles.modalTitleStyle}>
              Facilities for {openFacilities.manufacturer_name}
            </h2>
            <table style={styles.facilitiesTableStyle}>
              <thead>
                <tr style={styles.tableHeaderStyle}>
                  <th style={styles.facilitiesTableHeaderCellStyle}>
                    Facility Name
                  </th>
                  <th style={styles.facilitiesTableHeaderCellStyle}>City</th>
                  <th style={styles.facilitiesTableHeaderCellStyle}>State</th>
                  <th style={styles.facilitiesTableHeaderCellStyle}>Country</th>
                  <th style={styles.facilitiesTableHeaderCellStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {(openFacilities ? manufacturerFacilities : []).map(
                  (f: Facility) => (
                    <tr key={f.facility_id}>
                      <td style={styles.facilitiesTableCellStyle}>
                        {f.facility_name}
                      </td>
                      <td style={styles.facilitiesTableCellStyle}>{f.city}</td>
                      <td style={styles.facilitiesTableCellStyle}>
                        {f.state_province}
                      </td>
                      <td style={styles.facilitiesTableCellStyle}>
                        {f.country_code}
                      </td>
                      <td style={styles.facilitiesTableCellStyle}>
                        <button
                          onClick={() => {
                            setSelectedFacility(f);
                            setFacilityModalMode('EDIT');
                          }}
                          disabled={isViewer}
                          style={
                            isViewer
                              ? styles.buttonDisabled
                              : styles.buttonEnabled
                          }
                          onMouseEnter={e =>
                            isViewer
                              ? null
                              : Object.assign(
                                  e.currentTarget.style,
                                  styles.buttonEnabledHover
                                )
                          }
                          onMouseLeave={e =>
                            isViewer
                              ? null
                              : Object.assign(
                                  e.currentTarget.style,
                                  styles.buttonEnabled
                                )
                          }>
                          <Edit size={16} />
                        </button>
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
            <div style={styles.modalButtonsStyle}>
              <button
                onClick={() => setOpenFacilities(null)}
                style={styles.cancelButtonStyle}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Facility Modal */}
      {selectedFacility && (
        <FacilityModal
          facility={selectedFacility}
          initialMode={facilityModalMode || 'VIEW'}
          save={handleSaveFacility}
          close={() => {
            setSelectedFacility(null);
            setFacilityModalMode('VIEW');
          }}
        />
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  searchUploadContainerStyle: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    alignItems: 'flex-start',
  } as React.CSSProperties,
  titleStyle: {
    fontSize: '1.25rem', // text-xl
    fontWeight: 'bold',
    margin: 'auto',
  } as React.CSSProperties,
  tableHeaderStyle: {
    backgroundColor: '#f3f4f6', // bg-gray-100
  } as React.CSSProperties,
  facilitiesButtonStyle: {
    color: '#16a34a', // text-green-600
    cursor: 'pointer',
    background: 'none',
    border: 'none',
  } as React.CSSProperties,
  buttonEnabled: {
    color: '#d97706', // text-yellow-600
    cursor: 'pointer',
    background: 'none',
    border: 'none',
  } as React.CSSProperties,
  buttonEnabledHover: {
    color: '#92400e', // hover:text-yellow-800
  } as React.CSSProperties,
  buttonDisabled: {
    color: '#9ca3af', // text-gray-400
    cursor: 'not-allowed',
    background: 'none',
    border: 'none',
  } as React.CSSProperties,
  modalOverlayStyle: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 50,
  } as React.CSSProperties,
  facilitiesModalContentStyle: {
    backgroundColor: 'white',
    padding: '1.5rem', // p-6
    borderRadius: '0.5rem', // rounded-lg
    boxShadow:
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)', // shadow-lg
    width: '75%', // w-3/4
  } as React.CSSProperties,
  modalTitleStyle: {
    fontSize: '1.125rem', // text-lg
    fontWeight: 'bold',
    marginBottom: '1rem', // mb-4
  } as React.CSSProperties,
  modalButtonsStyle: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '1rem', // mt-4
  } as React.CSSProperties,
  cancelButtonStyle: {
    backgroundColor: '#d1d5db', // bg-gray-300
    padding: '0.5rem 1rem', // px-4 py-2
    borderRadius: '0.25rem', // rounded
    border: 'none',
    cursor: 'pointer',
  } as React.CSSProperties,
  facilitiesTableStyle: {
    width: '100%',
    border: '1px solid #e5e7eb',
  } as React.CSSProperties,
  facilitiesTableHeaderCellStyle: {
    padding: '0.5rem', // p-2
    border: '1px solid #e5e7eb',
    textAlign: 'left',
  } as React.CSSProperties,
  facilitiesTableCellStyle: {
    border: '1px solid #e5e7eb',
    padding: '0.5rem', // p-2
  } as React.CSSProperties,
  mergeButtonStyle: {
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    color: '#8b5cf6', // text-gray-500
    transition: 'color 0.2s',
  } as React.CSSProperties,
  mergeButtonHoverStyle: {
    color: '#6d28d9', // hover:text-purple-600
  } as React.CSSProperties,
  pageContainerStyle: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  } as React.CSSProperties,
  searchBarContainerStyle: {
    flexShrink: 0,
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #d1d5db',
    paddingBottom: '8px',
  } as React.CSSProperties,
  tableContainerStyle: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: 0,
  } as React.CSSProperties,
};

export default ManufacturerReview;
